'use client';

import Link from '@eliadmin/core/src/components/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { Newsletter } from '@eliadmin/core/src/components/homepage/newsletter';
import { EliLogo } from '@eliadmin/core/src/components/footer/eli-logo';
import { PartnersCarousel } from './partners-carousel';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { usePathname } from 'next/navigation';

export function FooterClient({
  partners,
  menus,
  generalLayout,
  GDPRlink,
  cities,
}: {
  partners: TDynamicData[];
  menus: Menu[];
  generalLayout: GeneralLayout['data'];
  GDPRlink: string;
  cities: TDynamicData[];
}) {
  const { t } = useTranslations();

  const pathname = usePathname();

  const isServiceCityPage = /\/sluzby\/[^/]+\/[^/]+/.test(pathname);

  return (
    <>
      {!isServiceCityPage && <PartnersCarousel partners={partners} />}
      {!isServiceCityPage && (
        <div className="block w-full lg:hidden">
          <Newsletter />
        </div>
      )}
      <div className={cn('custom-section', 'bg-black pt-16 sm:pt-24 lg:pt-[121px]')}>
        <div className={cn('custom-container', 'flex items-start justify-between gap-16')}>
          <div className="grid grid-cols-1 items-start justify-start gap-24 pb-8 sm:grid-cols-3 sm:pb-16 lg:flex lg:pb-0 xl:gap-32">
            {menus.map((menu, index) => (
              <div
                key={menu.name}
                className="hidden flex-col items-start justify-start gap-2 sm:flex"
              >
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{menu.name}</h2>
                {(menu.fields ?? []).map(field => (
                  <Link
                    key={field.name}
                    prefetch={false}
                    href={getSafeHref(field?.path ?? '#')}
                    className="flex items-center justify-start gap-1 whitespace-nowrap text-base text-white transition-all hover:opacity-80"
                  >
                    {field.name}
                    {(field.name === 'Kuchyne na mieru' ||
                      field.name === 'Maßgeschneiderte Küchen') && (
                      <span className="rounded-full bg-accent/30 p-0.5">
                        <div className="max-h-10 rounded-full bg-accent px-2 text-xs font-semibold text-black">
                          TOP
                        </div>
                      </span>
                    )}
                  </Link>
                ))}
                {index === 0 && (
                  <SocialIcons
                    className={{
                      wrapper: 'mt-4 flex flex-row items-center justify-start gap-4',
                      button:
                        'max-h-10 min-h-10 min-w-10 max-w-10 border-none bg-white shadow-none transition-all hover:bg-white/90',
                      icon: 'fill-black text-black',
                      iconLink: 'fill-black text-black',
                    }}
                  />
                )}
              </div>
            ))}
            <div className="flex w-full flex-col items-start justify-start gap-4">
              <div className="flex w-full flex-col items-start justify-start gap-2">
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{t('text157')}</h2>
                <p className="whitespace-nowrap text-sm text-white lg:text-base">
                  {generalLayout?.contact?.address}
                  <br />
                  {generalLayout?.contact?.postcode} {generalLayout?.contact?.city}
                </p>
              </div>
              <div className="flex w-full flex-col items-start justify-start gap-2">
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{t('text800')}</h2>
                <Link
                  prefetch={false}
                  href={`tel:${generalLayout?.contact?.phone}`}
                  target="_blank"
                  className="whitespace-nowrap text-sm text-white transition-all hover:opacity-80 lg:text-base"
                >
                  {generalLayout?.contact?.phone}
                </Link>
              </div>
              <div className="flex w-full flex-col items-start justify-start gap-2">
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{t('text2008')}</h2>
                <Link
                  prefetch={false}
                  href={`mailto:${generalLayout?.contact?.email}`}
                  target="_blank"
                  className="whitespace-nowrap text-sm text-white transition-all hover:opacity-80 lg:text-base"
                >
                  {generalLayout?.contact?.email}
                </Link>
              </div>
              <div className="flex w-full flex-col items-start justify-start gap-2">
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{t('text3592')}</h2>
                <p className="whitespace-nowrap text-sm text-white lg:text-base">
                  {generalLayout?.contact?.eori}
                </p>
              </div>
            </div>
            {isServiceCityPage && (
              <div className="flex w-full flex-col gap-3">
                <h2 data-ea-visual-tag="h5" className="text-sm text-primary-foreground">{t('text3592')}</h2>
                <p className="text-base text-white">PO-PIA: 8:00 - 16:00</p>
                <h2 data-ea-visual-tag="h5" className="mt-2 text-sm text-primary-foreground">Mestá kde pôsobíme</h2>
                {cities.slice(0, 5).map(city => (
                  <Link
                    key={city.name}
                    href={`/sluzby/kuchyne-na-mieru/${city.slug}`}
                    className="text-sm text-white/80 hover:opacity-80"
                  >
                    {city.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {!isServiceCityPage && (
            <div className="hidden w-full min-w-[384px] max-w-[384px] lg:block">
              <Newsletter />
            </div>
          )}
        </div>
        <div className={cn('custom-container', 'h-[1px] bg-white/20')} />
        <div
          className={cn(
            'custom-container',
            'mt-8 flex flex-col-reverse items-start justify-start gap-4 pb-16 sm:mt-10 sm:pb-[75px] md:flex-row md:items-center md:justify-between lg:mt-12',
          )}
        >
          <p className="text-sm text-white/70 sm:text-base">
            {`© Copyright ${new Date().getFullYear()} KAUFMANN`}
          </p>
          <div className="flex items-center justify-start gap-4 sm:gap-6 md:justify-end lg:gap-8">
            <Link
              prefetch={false}
              href={GDPRlink}
              className="text-sm text-white/70 underline transition-all hover:opacity-90 sm:text-base"
            >
              {t('text2403')}
            </Link>
            <Link
              prefetch={false}
              href={GDPRlink}
              className="text-sm text-white/70 underline transition-all hover:opacity-90 sm:text-base"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
      <div className="flex h-[52px] w-full items-center justify-center gap-4 bg-[#1C1C1C] sm:h-14">
        <p className="text-xs text-white/70 sm:text-sm md:text-base">{t('text639')}</p>
        <a
          href="https://elias-itsolutions.sk/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Eliaš IT Solutions"
          className={cn('w-fit', 'ea-footer__eli-logo-link')}
        >
          <EliLogo />
        </a>
      </div>
    </>
  );
}
